import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatFCFA, mediaSrc } from "@/lib/api";
import { BadgeCheck, Heart, Share2, Phone, MessageCircle, Play, MapPin, Package, Star, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import ProductCard from "@/components/Product/ProductCard";

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [img, setImg] = useState(0);
  const [isFav, setIsFav] = useState(false);
  const [orderOpen, setOrderOpen] = useState(false);
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState("");

  useEffect(() => {
    api.get(`/products/${id}`).then(r => { setP(r.data); setImg(0); });
    api.get(`/products/${id}/similar`).then(r => setSimilar(r.data || []));
  }, [id]);

  useEffect(() => {
    if (!user) return;
    api.get("/favorites/ids").then(r => setIsFav((r.data?.product_ids || []).includes(id))).catch(() => {});
  }, [user, id]);

  if (!p) return <div className="p-8 text-center text-[#73706B]">Chargement…</div>;

  const imgs = p.images || [];
  const shop = p.shop || {};

  const requireLogin = () => {
    if (!user) { toast.error("Connectez-vous pour continuer"); nav("/connexion"); return false; }
    return true;
  };

  const toggleFav = async () => {
    if (!requireLogin()) return;
    const { data } = await api.post("/favorites/toggle", { target_id: id, target_type: "product" });
    setIsFav(data.favorited);
  };

  const onShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: p.title, text: `${p.title} - ${formatFCFA(p.price)}`, url });
      else { await navigator.clipboard.writeText(url); toast.success("Lien copié !"); }
    } catch {}
  };

  const onWhatsApp = (e) => {
    if (!requireLogin()) { e?.preventDefault(); return; }
    api.post(`/products/${id}/click`).catch(() => {});
    api.post(`/events/contact`, { channel: "whatsapp", target_id: id, shop_id: shop.id }).catch(() => {});
    // Native <a> default-opens; we only block when login is missing.
  };
  const onCall = (e) => {
    if (!requireLogin()) { e?.preventDefault(); return; }
    api.post(`/products/${id}/click`).catch(() => {});
    api.post(`/events/contact`, { channel: "phone", target_id: id, shop_id: shop.id }).catch(() => {});
  };

  const waText = encodeURIComponent(`Bonjour, je suis intéressé par « ${p.title} » (${formatFCFA(p.price)}) vu sur Forum CI.`);
  const waDigits = (shop.whatsapp || "").replace(/\D/g, "");
  const waHref = `https://wa.me/${waDigits}?text=${waText}`;
  const telHref = `tel:${shop.whatsapp || ""}`;

  const submitOrder = async () => {
    if (!requireLogin()) return;
    try {
      await api.post("/orders", { product_id: id, quantity: qty, note });
      toast.success("Commande envoyée ! Le marchand sera notifié.");
      setOrderOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="pb-8">
      <button onClick={() => nav(-1)} className="mx-3 sm:mx-6 mt-3 h-10 w-10 rounded-full bg-white border border-[#E8E6E1] flex items-center justify-center" data-testid="back-btn">
        <ArrowLeft className="h-5 w-5" />
      </button>

      <div className="max-w-5xl mx-auto px-3 sm:px-6 pt-3 grid md:grid-cols-2 gap-4 sm:gap-8">
        {/* Gallery */}
        <div>
          <div className="aspect-square rounded-2xl overflow-hidden bg-[#F3F1EC] border border-[#E8E6E1] relative">
            {p.video && img === imgs.length ? (
              <video controls src={mediaSrc(p.video)} className="w-full h-full object-contain" />
            ) : imgs[img] ? (
              <img src={mediaSrc(imgs[img])} alt={p.title} className="w-full h-full object-contain" />
            ) : null}
          </div>
          <div className="mt-2 flex gap-2 overflow-x-auto scrollbar-hide">
            {imgs.map((i, k) => (
              <button key={k} onClick={() => setImg(k)} className={`shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 ${img === k ? "border-[#E65100]" : "border-[#E8E6E1]"}`}>
                <img src={mediaSrc(i)} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
            {p.video && (
              <button onClick={() => setImg(imgs.length)} className={`shrink-0 w-16 h-16 rounded-lg border-2 flex items-center justify-center bg-[#1A1918] text-white ${img === imgs.length ? "border-[#E65100]" : "border-[#E8E6E1]"}`}>
                <Play className="h-5 w-5 fill-current" />
              </button>
            )}
          </div>
        </div>

        {/* Info */}
        <div>
          <h1 className="font-[Manrope] font-bold text-2xl sm:text-3xl leading-tight text-[#1A1918]">{p.title}</h1>
          <div className="mt-3 flex items-baseline gap-3">
            <div className="font-extrabold text-3xl text-[#E65100]">{formatFCFA(p.price)}</div>
            {p.wholesale_price && <div className="text-sm text-[#73706B]">Gros : {formatFCFA(p.wholesale_price)}</div>}
          </div>
          <div className="mt-2 flex items-center gap-2 text-sm text-[#73706B]">
            <Package className="h-4 w-4" /> Stock : {p.stock} · {p.condition === "neuf" ? "Neuf" : "Occasion"}
            {p.wholesale && <span className="ml-1 bg-[#1A1918] text-white text-[10px] font-bold px-2 py-0.5 rounded">GROS</span>}
          </div>

          <div className="flex gap-2 mt-4">
            <Button onClick={toggleFav} variant="outline" className="h-11 rounded-xl" data-testid="fav-toggle-btn">
              <Heart className={`h-5 w-5 ${isFav ? "fill-[#E65100] text-[#E65100]" : ""}`} />
            </Button>
            <Button onClick={onShare} variant="outline" className="h-11 rounded-xl" data-testid="share-btn">
              <Share2 className="h-5 w-5" />
            </Button>
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onWhatsApp}
              data-testid="whatsapp-btn"
              className="h-11 rounded-xl flex-1 bg-[#0A7D3A] hover:bg-[#086a31] inline-flex items-center justify-center text-white font-medium px-3"
            >
              <MessageCircle className="h-5 w-5 mr-1" /> WhatsApp
            </a>
            <a
              href={telHref}
              onClick={onCall}
              data-testid="call-btn"
              className="h-11 rounded-xl border border-[#E8E6E1] inline-flex items-center justify-center px-3"
            >
              <Phone className="h-5 w-5" />
            </a>
          </div>

          {/* Shop trust card */}
          <Link to={`/boutique/${shop.id}`} className="mt-5 flex items-center gap-3 p-3 rounded-xl border border-[#E8E6E1] hover:border-[#E65100] transition" data-testid="shop-trust-card">
            <div className="w-12 h-12 rounded-xl bg-[#F3F1EC] overflow-hidden shrink-0">
              {shop.profile_image ? <img src={mediaSrc(shop.profile_image)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center font-bold">{shop.name?.[0]}</div>}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1"><div className="font-bold truncate">{shop.name}</div>{shop.verified && <BadgeCheck className="h-4 w-4 text-[#0A7D3A]" />}</div>
              <div className="flex items-center gap-2 text-xs text-[#73706B] mt-0.5">
                {shop.city && <><MapPin className="h-3 w-3" />{shop.city}</>}
                {shop.rating_count > 0 && <><Star className="h-3 w-3 fill-[#E65100] text-[#E65100] ml-1" />{shop.rating_avg.toFixed(1)} ({shop.rating_count})</>}
              </div>
            </div>
          </Link>

          {p.description && (
            <div className="mt-5">
              <div className="font-bold mb-1">Description</div>
              <div className="text-sm leading-relaxed text-[#1A1918] whitespace-pre-wrap">{p.description}</div>
            </div>
          )}
        </div>
      </div>

      {similar.length > 0 && (
        <div className="max-w-5xl mx-auto px-3 sm:px-6 mt-10">
          <h2 className="font-[Manrope] font-bold text-xl mb-3">Similaires</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {similar.map(s => <ProductCard key={s.id} product={s} />)}
          </div>
        </div>
      )}
    </div>
  );
}
